import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import {
  ArrowRight,
  Calendar,
  CalendarCheck,
  CalendarFold,
  ClipboardCheck,
  PlaneTakeoff,
  Route,
} from '@/components/icons'
import { AppLayout } from './AppLayout'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Skeleton } from '@/components/ui/skeleton'
import { StatCard } from '@/components/ui/stat-card'
import { SectionTitle } from './ui/section-title'
import { CrewAcceptanceBadge } from './CrewAcceptanceBadge'
import { EmptyState } from './EmptyState'
import { FlightDrawer } from './FlightDrawer'
import { DutyBadge, formatDutyStatus } from './duty-status'
import { hapticLight } from '@/lib/haptics'

import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember'
import {
  useRosterGetSummary,
  useRosterGetByCrew,
} from '../lib/hooks/roster/roster.hooks.queries'
import {
  useTripGetMyUpcomingTrips,
  useTripAcknowledgeMyTrip,
} from '../lib/hooks/trip'
import { useAirportsByIcaos } from '../lib/hooks/airport'
import { useMyDraftTasks } from '../lib/hooks/task'
import {
  formatFlightDuration,
  formatLocalDate,
  formatLocalTime,
} from '../lib/flight-utils'
import type { Airport } from '../lib/airport-db'

dayjs.extend(relativeTime)

export function MySchedule() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const { data: currentUser } = useCurrentCrewMember()
  const crewId = currentUser?.id

  const today = useMemo(() => dayjs(), [])
  const monthStart = today.startOf('month').format('YYYY-MM-DD')
  const monthEnd = today.endOf('month').format('YYYY-MM-DD')
  const todayStr = today.format('YYYY-MM-DD')
  // Forward window the roster card walks to find the end of today's duty run.
  const dutyWindowEnd = today.add(45, 'day').format('YYYY-MM-DD')

  const { data: summary, isPending: summaryLoading } = useRosterGetSummary(
    crewId ?? '',
    monthStart,
    monthEnd
  )

  const { data: rosterDays = [] } = useRosterGetByCrew(crewId ?? '', {
    startDate: todayStr,
    endDate: dutyWindowEnd,
  })
  const currentDutyStatus = rosterDays.find(
    (day: RosterDay) => day.date === todayStr
  )?.dutyStatus

  const { data: trips = [], isPending: tripsLoading } = useTripGetMyUpcomingTrips(
    !!crewId
  )
  const acceptTrip = useTripAcknowledgeMyTrip()

  const { data: draftTasks = [], isPending: tasksLoading } = useMyDraftTasks()
  const draftTasksCount = draftTasks.length

  // Tapping a flight opens the read-only flight sheet over this page rather than
  // navigating away, so the expanded trip is still there when it's dismissed.
  // `openLeg` deliberately outlives `isFlightDrawerOpen`: clearing the ids on
  // close would blank the sheet mid-animation. It also gates mounting — the
  // drawer does not early-return on `!isOpen`, so an always-mounted instance
  // would fire its trip/aircraft/passenger/checklist queries on every render.
  const [openLeg, setOpenLeg] = useState<{
    tripId: string
    legId: string
  } | null>(null)
  const [isFlightDrawerOpen, setIsFlightDrawerOpen] = useState(false)

  // Legs still to fly, across every upcoming trip. The payload carries the whole
  // trip, so already-flown legs are filtered back out here.
  const upcomingLegCount = useMemo(() => {
    const now = Date.now()
    return trips.reduce(
      (total, trip) =>
        total +
        trip.legs.filter(
          (leg) => new Date(leg.arrivalDateTime).getTime() >= now
        ).length,
      0
    )
  }, [trips])

  const isInitialLoading =
    !crewId || summaryLoading || tripsLoading || tasksLoading

  return (
    <>
      <AppLayout
        topBar={
          isDesktop ? (
            <div className="px-gutter pt-8 pb-2">
              <DesktopPageHeader title="My Schedule" />
            </div>
          ) : (
            <ScheduleTopBar />
          )
        }
        wrapperClassName="bg-surface-brand-L0"
        className="bg-surface-brand-L0 pt-2"
      >
        {isInitialLoading ? (
          <div className="flex flex-col gap-6 pb-28">
            {/* Stats cards */}
            <div className="flex gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-[88px] flex-1 rounded-2xl" />
              ))}
            </div>

            {/* My Roster */}
            <Skeleton className="h-20 w-full rounded-2xl" />

            {/* Upcoming Trips */}
            <section className="flex flex-col gap-2">
              <Skeleton className="h-4 w-36" />
              <Card className="flex flex-col gap-0 p-0 overflow-hidden">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i}>
                    <div className="flex flex-col gap-1 px-4 py-3">
                      <Skeleton className="h-4 w-44" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                    {i < 2 && <div className="h-px bg-border-L1 mx-4" />}
                  </div>
                ))}
              </Card>
            </section>

            {/* Tasks */}
            <section className="flex flex-col gap-2">
              <Skeleton className="h-4 w-20" />
              <Card className="flex flex-col gap-0 p-0 overflow-hidden">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i}>
                    <div className="flex flex-col gap-1 px-4 py-3">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-28" />
                    </div>
                    {i < 2 && <div className="h-px bg-border-L1 mx-4" />}
                  </div>
                ))}
              </Card>
            </section>
          </div>
        ) : (
          <div className="flex flex-col gap-6 pb-28">
            <ScheduleStats
              onDutyDays={summary?.totalOnDutyDays ?? 0}
              flightsCount={upcomingLegCount}
              tasksCount={draftTasksCount}
            />

            <RosterCard
              dutyStatus={currentDutyStatus ?? 'OffDuty'}
              subtitle={dutySubtitle(rosterDays, todayStr)}
              onClick={() =>
                crewId &&
                navigate({
                  to: '/roster/$crewId',
                  params: { crewId },
                })
              }
            />

            <UpcomingTripsSection
              trips={trips}
              onAccept={(tripId) => acceptTrip.mutate({ tripId })}
              acceptingTripId={
                acceptTrip.isPending ? acceptTrip.variables?.tripId : undefined
              }
              onLegClick={(tripId, legId) => {
                setOpenLeg({ tripId, legId })
                setIsFlightDrawerOpen(true)
              }}
            />

            <TasksSection
              tasks={draftTasks}
              draftCount={draftTasksCount}
              onTaskClick={(taskId) =>
                navigate({ to: '/task/$taskId', params: { taskId } })
              }
            />
          </div>
        )}
      </AppLayout>

      {/* Outside AppLayout: the sheet is position-fixed and has no business
          living inside the page's overflow-y-auto scroll container. */}
      {openLeg && (
        <FlightDrawer
          isOpen={isFlightDrawerOpen}
          onClose={() => setIsFlightDrawerOpen(false)}
          tripId={openLeg.tripId}
          tripLegId={openLeg.legId}
          mode="edit"
          readOnly
        />
      )}
    </>
  )
}

// ============ TOP BAR ============

function ScheduleTopBar() {
  return (
    <>
      <div className="shrink-0 safe-top">
        <div className="h-16" />
      </div>
      <div className="bg-surface-brand-L0 fixed top-0 left-0 right-0 z-50 safe-top">
        <div className="px-4 py-4 flex items-center justify-between">
          <h1 className="text-content-primary-bold text-xl font-semibold">
            My Schedule
          </h1>
        </div>
      </div>
    </>
  )
}

// ============ STATS CARDS ============

function ScheduleStats({
  onDutyDays,
  flightsCount,
  tasksCount,
}: {
  onDutyDays: number
  flightsCount: number
  tasksCount: number
}) {
  return (
    <div className="flex gap-2">
      <StatCard
        variant="brand"
        className="flex-1"
        icon={<Calendar />}
        label="ON DUTY"
        value={onDutyDays}
        caption="This month"
      />
      <StatCard
        variant="brand"
        className="flex-1"
        icon={<PlaneTakeoff />}
        label="FLIGHTS"
        value={flightsCount}
        caption="This month"
      />
      <StatCard
        variant="brand"
        className="flex-1"
        icon={<ClipboardCheck />}
        label="TASKS"
        value={tasksCount}
        caption="This month"
      />
    </div>
  )
}

// ============ MY ROSTER ============

type RosterDay = {
  date: string
  dutyStatus: string
}

// "On Duty until 11 Apr" — the last day of the unbroken run of today's duty
// status. Falls back to the bare status when the run is only today, or when
// there's no roster entry for today at all.
function dutySubtitle(entries: RosterDay[], todayStr: string): string {
  const byDate = new Map(entries.map((entry) => [entry.date, entry]))
  const todayEntry = byDate.get(todayStr)
  if (!todayEntry) return formatDutyStatus('OffDuty')

  let lastDate = todayStr
  for (;;) {
    // RosterEntry.date is a YYYY-MM-DD string, so step it as a calendar day —
    // never through a Date, which would shift across timezones.
    const nextDate = dayjs(lastDate).add(1, 'day').format('YYYY-MM-DD')
    if (byDate.get(nextDate)?.dutyStatus !== todayEntry.dutyStatus) break
    lastDate = nextDate
  }

  const label = formatDutyStatus(todayEntry.dutyStatus)
  return lastDate === todayStr
    ? label
    : `${label} until ${dayjs(lastDate).format('D MMM')}`
}

function RosterCard({
  dutyStatus,
  subtitle,
  onClick,
}: {
  dutyStatus: string
  subtitle: string
  onClick: () => void
}) {
  return (
    <Card
      interactive
      onClick={onClick}
      className="flex items-center justify-between gap-3 px-4 py-4"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="bg-background-brand-subtle rounded-lg p-2 flex items-center shrink-0">
          <CalendarFold className="size-8 text-content-brand-bold" />
        </div>
        <div className="flex flex-col min-w-0">
          <p className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px]">
            My Roster
          </p>
          <p className="text-sm text-content-primary-subtle leading-5 truncate">
            {subtitle}
          </p>
        </div>
      </div>
      <DutyBadge status={dutyStatus} />
    </Card>
  )
}

// ============ UPCOMING TRIPS ============

type TripLegRow = {
  id: string
  departureAirportIcao: string
  arrivalAirportIcao: string
  departureDateTime: string | Date
  arrivalDateTime: string | Date
  acknowledged: boolean
}

type UpcomingTrip = {
  tripId: string
  tripName: string
  firstDepartureDateTime: string | Date
  lastArrivalDateTime: string | Date
  accepted: boolean
  legs: TripLegRow[]
}

function UpcomingTripsSection({
  trips,
  onAccept,
  acceptingTripId,
  onLegClick,
}: {
  trips: UpcomingTrip[]
  onAccept: (tripId: string) => void
  acceptingTripId: string | undefined
  onLegClick: (tripId: string, legId: string) => void
}) {
  // Trips expand independently — any number can be open at once.
  const [openTripIds, setOpenTripIds] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const setTripOpen = (tripId: string, open: boolean) =>
    setOpenTripIds((prev) => {
      const next = new Set(prev)
      if (open) next.add(tripId)
      else next.delete(tripId)
      return next
    })

  // Airport names and timezones come from the on-device airport DB, so this
  // costs no round trip. Resolved once for the section, not once per row.
  const icaos = useMemo(
    () => [
      ...new Set(
        trips.flatMap((trip) =>
          trip.legs.flatMap((leg) => [
            leg.departureAirportIcao,
            leg.arrivalAirportIcao,
          ])
        )
      ),
    ],
    [trips]
  )
  const airports = useAirportsByIcaos(icaos)
  const airportsByIcao = useMemo(
    () => new Map(airports.map((airport) => [airport.icaoCode, airport])),
    [airports]
  )

  return (
    <section className="flex flex-col gap-2">
      <SectionTitle
        label="Upcoming Trips"
        count={trips.length}
        icon={<Route className="w-5 h-5 text-content-brand-bold" />}
        className="pb-0"
      />
      {trips.length === 0 ? (
        <Card className="flex items-center justify-center px-4 py-8">
          <p className="text-sm text-content-primary-subtle">
            No upcoming trips.
          </p>
        </Card>
      ) : (
        <Card className="flex flex-col gap-0 p-0 overflow-hidden">
          {trips.map((trip, idx) => (
            <div key={trip.tripId}>
              <UpcomingTripRow
                trip={trip}
                open={openTripIds.has(trip.tripId)}
                onOpenChange={(open) => setTripOpen(trip.tripId, open)}
                onAccept={() => onAccept(trip.tripId)}
                accepting={acceptingTripId === trip.tripId}
                airportsByIcao={airportsByIcao}
                onLegClick={(legId) => onLegClick(trip.tripId, legId)}
              />
              {idx < trips.length - 1 && (
                <div className="h-px bg-border-L1 mx-4" />
              )}
            </div>
          ))}
        </Card>
      )}
    </section>
  )
}

function UpcomingTripRow({
  trip,
  open,
  onOpenChange,
  onAccept,
  accepting,
  airportsByIcao,
  onLegClick,
}: {
  trip: UpcomingTrip
  open: boolean
  onOpenChange: (open: boolean) => void
  onAccept: () => void
  accepting: boolean
  airportsByIcao: Map<string, Airport>
  onLegClick: (legId: string) => void
}) {
  const first = dayjs(trip.firstDepartureDateTime)
  const last = dayjs(trip.lastArrivalDateTime)

  const summary = (
    <div className="flex flex-col min-w-0">
      <p className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px] truncate">
        {trip.tripName}
      </p>
      <div className="flex items-center gap-1 text-xs font-semibold text-content-primary-subtle leading-4">
        <span>{first.format('D MMM')}</span>
        {!first.isSame(last, 'day') && (
          <>
            <ArrowRight className="size-3 text-content-primary-subtle-mid" />
            <span>{last.format('D MMM')}</span>
          </>
        )}
      </div>
    </div>
  )

  // Sibling of the trigger, never nested inside it — a button within a button is
  // invalid markup, and accepting must not also toggle the row.
  const chip = trip.accepted ? (
    <CrewAcceptanceBadge accepted />
  ) : (
    <Button
      variant="brandSubtle"
      size="mini"
      roundness="round"
      className="h-6 px-2 shrink-0"
      loading={accepting}
      onClick={(e) => {
        e.stopPropagation()
        onAccept()
      }}
    >
      Accept
    </Button>
  )

  // A trip we hold no legs on can't expand — render the summary alone rather
  // than a tappable row that opens an empty panel.
  if (trip.legs.length === 0) {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        {summary}
        {chip}
      </div>
    )
  }

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            onClick={() => hapticLight()}
            className="flex-1 min-w-0 text-left active:opacity-60 transition-opacity"
          >
            {summary}
          </button>
        </CollapsibleTrigger>
        {chip}
      </div>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <div className="bg-surface-brand-L1">
          {trip.legs.map((leg, idx) => (
            <div key={leg.id}>
              {idx > 0 && <div className="h-px bg-border-L1 mx-4" />}
              <TripFlightRow
                leg={leg}
                airportsByIcao={airportsByIcao}
                onClick={() => onLegClick(leg.id)}
              />
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function TripFlightRow({
  leg,
  airportsByIcao,
  onClick,
}: {
  leg: TripLegRow
  airportsByIcao: Map<string, Airport>
  onClick: () => void
}) {
  const departure = airportsByIcao.get(leg.departureAirportIcao)
  const arrival = airportsByIcao.get(leg.arrivalAirportIcao)
  const departureCity =
    departure?.municipality || departure?.name || leg.departureAirportIcao
  const arrivalCity =
    arrival?.municipality || arrival?.name || leg.arrivalAirportIcao

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 min-h-[60px] active:opacity-60 transition-opacity"
    >
      <div className="flex flex-col min-w-0">
        <div className="flex items-center gap-2 text-sm font-semibold text-content-primary-bold leading-5">
          <span>{leg.departureAirportIcao}</span>
          <ArrowRight className="size-3 shrink-0 text-content-primary-subtle-mid" />
          <span>{leg.arrivalAirportIcao}</span>
        </div>
        <div className="flex items-center gap-1 min-w-0 text-xs font-semibold text-content-primary-subtle leading-4">
          <span className="truncate">{departureCity}</span>
          <ArrowRight className="size-2 shrink-0 text-content-primary-subtle-mid" />
          <span className="truncate">{arrivalCity}</span>
          <span className="shrink-0">
            {'•'}{' '}
            {formatFlightDuration(leg.departureDateTime, leg.arrivalDateTime)}
          </span>
        </div>
      </div>
      <div className="flex flex-col shrink-0 text-xs font-semibold leading-4">
        <span className="text-content-primary-subtle">Departs</span>
        <span className="flex items-center gap-1 text-content-primary-bold">
          <span>
            {formatLocalDate(leg.departureDateTime, departure?.timezone)}
          </span>
          <span>
            {formatLocalTime(leg.departureDateTime, departure?.timezone)}
          </span>
        </span>
      </div>
    </button>
  )
}

// ============ TASKS ============

type TaskRow = {
  id: string
  name: string
  status: string
  createdAt: string | Date
  tripLeg?: {
    departureAirportIcao?: string | null
    arrivalAirportIcao?: string | null
    trip?: { tripName?: string | null } | null
  } | null
}

function TasksSection({
  tasks,
  draftCount,
  onTaskClick,
}: {
  tasks: TaskRow[]
  draftCount: number
  onTaskClick: (taskId: string) => void
}) {
  return (
    <section className="flex flex-col gap-2">
      <SectionTitle
        label="My Tasks"
        count={draftCount}
        icon={<ClipboardCheck className="w-5 h-5 text-content-brand-bold" />}
        className="pb-0"
      />
      {tasks.length === 0 ? (
        <EmptyState
          icon={CalendarCheck}
          title="No Tasks"
          description="You have no tasks yet."
        />
      ) : (
        <Card className="flex flex-col gap-0 p-0 overflow-hidden">
          {tasks.map((task, idx) => (
            <div key={task.id}>
              <TaskRowContent
                task={task}
                onClick={() => onTaskClick(task.id)}
              />
              {idx < tasks.length - 1 && (
                <div className="h-px bg-border-L1 mx-4" />
              )}
            </div>
          ))}
        </Card>
      )}
    </section>
  )
}

function TaskRowContent({
  task,
  onClick,
}: {
  task: TaskRow
  onClick: () => void
}) {
  const tripName = task.tripLeg?.trip?.tripName ?? null
  const depIcao = task.tripLeg?.departureAirportIcao ?? null
  const arrIcao = task.tripLeg?.arrivalAirportIcao ?? null
  const isDraft = task.status === 'Draft'

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex flex-col gap-1 px-4 py-3"
    >
      <div className="flex items-center gap-2">
        <p className="text-sm font-bold text-content-primary-bold leading-5">
          {task.name}
        </p>
        {isDraft && (
          <span className="size-2 shrink-0 rounded-full bg-content-info-bold" />
        )}
      </div>
      {(tripName || depIcao) && (
        <div className="flex items-center gap-3 text-sm text-content-primary-subtle leading-5">
          {tripName && <span className="truncate">{tripName}</span>}
          {depIcao && arrIcao && (
            <span className="flex items-center gap-2 shrink-0">
              <span>{depIcao}</span>
              <ArrowRight className="size-3 text-content-primary-subtle-mid" />
              <span>{arrIcao}</span>
            </span>
          )}
        </div>
      )}
      <p className="text-xs font-semibold text-content-primary-subtle leading-4">
        {dayjs(task.createdAt).fromNow()}
      </p>
    </button>
  )
}
